import { watch } from '@vue/composition-api';
import { AgentStatusResponse, useAgents } from '@/modules/agents-store';
import { useWSEventsWorker, EventData, EVENT_TYPE } from '@/workers/ws-events';

export function useAgentStatusPolling() {
  const { subscribe, initializeStatusPings } = useWSEventsWorker();
  const {
    activeAgentStatusKey,
    activeAgentStatus,
    activeAgentStatusLabel,
    statusUpdateTimestamp,
    isStatusInitializing,
    setAgentStatus,
    reInitializeActiveAgentStatus,
  } = useAgents();

  /**
   * By default status on client should match status on server.
   * If for some reason there is a desync - try to set status again.
   */
  const onPingAgentStatus = async (data: EventData<AgentStatusResponse>) => {
    try {
      const { key = '', status, label = '', lastStatusUpdate } = data.content;
      const isKeyEqual = activeAgentStatusKey.value === key;
      const isStatusEqual = status === activeAgentStatus.value;
      const isLabelEqual = label === activeAgentStatusLabel.value;

      if (isKeyEqual && isStatusEqual && isLabelEqual) {
        return;
      }

      /* If current status displayed in UI is newer than ping return value, ignore ping */
      if (statusUpdateTimestamp.value) {
        const ping = new Date(lastStatusUpdate || 0).getTime();
        if (statusUpdateTimestamp.value >= ping) {
          return;
        }
      }

      /* If there is a different status on server, set it as current */
      setAgentStatus({ status: data.content });
    } catch (e) {
      /* If there is no status in server cache, set current status again */
      await reInitializeActiveAgentStatus();
    }
  };

  const subscribeToStatusPolling = () => {
    /* Notify worker that agent's status is ready */
    watch(isStatusInitializing, (current, previous) => {
      if (!current && previous) {
        initializeStatusPings();
      }
    }, { immediate: true });

    subscribe(EVENT_TYPE.PING_STATUS, onPingAgentStatus);
  };

  return { subscribeToStatusPolling };
}
